// Result-set → spatial-column categorization for the Map view (T13, shared with T14). Mirrors the
// grid's native-spatial detection (categorizeSpatialColumn + SQLite BLOB sniff) but keyed on a
// `QueryResultSet` rather than the grid's normalized columns. EAGER-safe: the only decode-side
// import is the header sniffer (via spatialColumnCategorization) — never wkx / the decoder / maplibre.

import { useMemo } from 'react';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { DatabaseMetaInfo } from '../../../../types/electron';
import type { QueryColumn, QueryResultSet } from '../../../../types/queryExecution';
import { engineSupports } from '../../../../core/engines/engineCapabilities';
import {
  categorizeSpatialColumn,
  sniffSpatialBlobColumns,
  type SpatialColumnInput,
} from '../../common/utils/spatialColumnCategorization';
import type { SpatialColumnDeclaration } from '../../common/utils/spatialValueAccessor';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';

/** Row key / stable id for a result column (matches `QueryResultSet.rows` keying). */
export function resultColumnId(column: QueryColumn): string {
  return column.id ?? column.name;
}

export type SpatialResultColumns = {
  /** Geometry-column candidates for the map — native-spatial AND declared (T18). */
  spatialColumnIds: string[];
  /** columnId → engine data-type string. */
  columnDataTypes: Map<string, string>;
  /** columnId → display label. */
  columnLabels: Map<string, string>;
  /** Non-spatial column ids — label / tooltip / color-by / timestamp (T31) candidates. */
  nonSpatialColumnIds: string[];
  /** T18 — declared coordinate columns among the candidates (columnId → declaration).
   *  Feeds the map's decode via the declared arm; a pair keys only its LAT column. */
  declaredSpatialColumns: Map<string, SpatialColumnDeclaration>;
};

const EMPTY: SpatialResultColumns = {
  spatialColumnIds: [],
  columnDataTypes: new Map(),
  columnLabels: new Map(),
  nonSpatialColumnIds: [],
  declaredSpatialColumns: new Map(),
};

/**
 * Pure: derive the native-spatial and non-spatial column sets of `resultSet`. Gated by the engine's
 * `supportsSpatialTypes` capability (kusto opts out entirely). SQLite additionally promotes
 * BLOB/affinity-less columns that sniff as SpatiaLite/GeoPackage over the loaded rows (zero I/O).
 */
export function getSpatialResultColumns(
  resultSet: QueryResultSet | undefined,
  engine: DatabaseEngineId | null | undefined,
  metaInfo: DatabaseMetaInfo | null | undefined,
): SpatialResultColumns {
  if (!resultSet || !engine) return EMPTY;
  const columnDataTypes = new Map<string, string>();
  const columnLabels = new Map<string, string>();
  for (const column of resultSet.columns) {
    const id = resultColumnId(column);
    columnDataTypes.set(id, column.dataType ?? '');
    columnLabels.set(id, column.name);
  }

  const spatial = new Set<string>();
  // T18 — declared coordinate columns are geometry candidates on ANY engine,
  // even ones with no native spatial capability. Native detection below is
  // gated by the capability; declared detection runs unconditionally.
  const declaredSpatialColumns = new Map<string, SpatialColumnDeclaration>();
  for (const column of resultSet.columns) {
    if (!column.sourceTable || !column.sourceColumn) continue;
    const decl = (metaInfo?.spatialColumns ?? []).find(
      (d) =>
        d.schema === (column.sourceSchema ?? '') &&
        d.table === column.sourceTable &&
        d.column === column.sourceColumn,
    );
    if (decl) {
      const id = resultColumnId(column);
      declaredSpatialColumns.set(id, decl);
      spatial.add(id);
    }
  }

  if (engineSupports(engine, 'spatial', 'supportsSpatialTypes')) {
    for (const column of resultSet.columns) {
      if (!column.sourceTable || !column.sourceColumn || !column.dataType) continue;
      const input: SpatialColumnInput = {
        engine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable,
        name: column.sourceColumn,
        dataType: column.dataType,
      };
      if (categorizeSpatialColumn(input, metaInfo) === 'native-spatial') {
        spatial.add(resultColumnId(column));
        declaredSpatialColumns.delete(resultColumnId(column)); // native wins
      }
    }
    if (engine === 'sqlite') {
      const blobInputs: SpatialColumnInput[] = resultSet.columns.map((column) => ({
        engine,
        schema: column.sourceSchema ?? '',
        table: column.sourceTable ?? '',
        name: resultColumnId(column),
        dataType: column.dataType ?? '',
      }));
      const promoted = sniffSpatialBlobColumns(blobInputs, resultSet.rows.slice(0, 20));
      for (const column of resultSet.columns) {
        if (promoted.has(resultColumnId(column))) {
          spatial.add(resultColumnId(column));
          declaredSpatialColumns.delete(resultColumnId(column));
        }
      }
    }
  }

  return {
    spatialColumnIds: resultSet.columns.map(resultColumnId).filter((id) => spatial.has(id)),
    columnDataTypes,
    columnLabels,
    nonSpatialColumnIds: resultSet.columns.map(resultColumnId).filter((id) => !spatial.has(id)),
    declaredSpatialColumns,
  };
}

/** Hook wrapper — subscribes to the connection/database MetaInfo and memoizes the categorization. */
export function useSpatialResultColumns(
  resultSet: QueryResultSet | undefined,
  engine: DatabaseEngineId | null | undefined,
  connectionId: string | null | undefined,
  database: string | null | undefined,
): SpatialResultColumns {
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);
  return useMemo(
    () => getSpatialResultColumns(resultSet, engine, metaInfo),
    [resultSet, engine, metaInfo],
  );
}
